/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { LOCALSTORAGE_PIN_KEY_PREFIX } from 'src/app/conf/permissions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { EPermissionStatus } from 'src/app/model/permission';
import { AppState } from 'src/app/store/app.reducer';
import * as PermissionActions from './permissions.actions';
import { UserFileService } from 'src/app/services/user-file.service';
import { PermissionService } from 'src/app/services/permission.service';
import * as NotificationsActions from '../notifications/notifications.actions';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Injectable()
export class PermissionEffects {
    getPermissions$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.getPermissions),
        withLatestFrom(this.store.select('site', 'currentSite')),
        switchMap(([action, currentSite]) => this.permissionService.getPermissions(currentSite.Id, action.paging, action.filter,
            action.activeFilter, action.readyFilter, action.expiredFilter, action.createdFilter).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return PermissionActions.getPermissionsSuccess({
                            permissions: resp.Data.Items,
                            paging: resp.Data.Paging
                        });
                    } else {
                        return PermissionActions.getPermissionsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(PermissionActions.getPermissionsFailed({ errorMessage: error.message })))
            ))
    ));

    getPermissionsCalendar$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.getPermissionsCalendar),
        withLatestFrom(this.store.select('site', 'currentSite')),
        switchMap(([action, currentSite]) => this.permissionService.getPermissionsCalendar(currentSite.Id, action.datefrom, action.dateto).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.getPermissionsCalendarSuccess({
                        permissionsCalendar: resp.Data.Items,
                        paging: resp.Data.Paging
                    });
                } else {
                    return PermissionActions.getPermissionsCalendarFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.getPermissionsCalendarFailed({ errorMessage: error.message })))
        ))
    ));

    getPermittedCommands$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.getPermittedCommands),
        withLatestFrom(this.store.select('site', 'currentSite')),
        switchMap(([action, currentSite]) => this.permissionService.getPermittedCommands(currentSite.Id).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.getPermittedCommandsSuccess({ permittedCommands: resp.Data });
                } else {
                    return PermissionActions.getPermittedCommandsFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.getPermittedCommandsFailed({ errorMessage: error.message })))
        ))
    ));

    getPermission$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.getPermission),
        switchMap((action) => this.permissionService.getPermission(action.permissionId).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.getPermissionSuccess({ permission: resp.Data });
                } else {
                    return PermissionActions.getPermissionFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.getPermissionFailed({ errorMessage: error.message })))
        ))
    ));

    addPermission$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.addPermission),
        withLatestFrom(this.store.select('site', 'currentSite')),
        switchMap(([action, currentSite]) => this.permissionService.addPermission(
            currentSite.Id,
            action.permission,
            action.permissionCommands
        ).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.addPermissionSuccess({
                        permission: { ...action.permission, Id: resp.Data.Id, Status: EPermissionStatus.Created, },
                        message: this.translate.instant('ADD_PERMISSION_SUCCESS'),
                    });

                } else {
                    return PermissionActions.addPermissionFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.addPermissionFailed({ errorMessage: error.message })))
        ))
    ));

    addPermissionSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.addPermissionSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_permission_completed', params: { page_title: 'Permissions' } }));
            }, 500);
        }),
    ), { dispatch: false });

    editPermission$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.editPermission),
        switchMap((action) => this.permissionService.editPermission(action.permission, action.permissionCommands).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.editPermissionSuccess({
                        permission: action.permission,
                        message: this.translate.instant('EDIT_PERMISSION_SUCCESS'),
                    });
                } else {
                    return PermissionActions.editPermissionFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.editPermissionFailed({ errorMessage: error.message })))
        ))
    ));

    deletePermission$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.deletePermission),
        switchMap((action) => this.permissionService.deletePermission(action.permission.Id).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.deletePermissionSuccess({
                        permissionId: resp.Data.Id,
                        message: this.translate.instant('DELETE_PERMISSION_SUCCESS', { name: action.permission.Name }),
                    });
                } else {
                    return PermissionActions.deletePermissionFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.deletePermissionFailed({ errorMessage: error.message })))
        ))
    ));

    /*reloadRemainingPermissions$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.deletePermission, PermissionActions.addPermission),
        switchMap(() => [
            PermissionActions.getPermissions({}),
        ])
    ));*/

    resetPin$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.resetPin),
        switchMap((action) => this.permissionService.resetPin(action.permissionId).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.resetPinSuccess({ message: this.translate.instant('PIN_RESET_SUCCESS') });
                } else {
                    return PermissionActions.resetPinFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.resetPinFailed({ errorMessage: error.message })))
        ))
    ));

    setPin$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.setPin),
        switchMap((action) => this.permissionService.setPin(action.token, action.pin).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.setPinSuccess({ message: this.translate.instant('PIN_SET_SUCCESS') });
                } else {
                    return PermissionActions.setPinFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.setPinFailed({ errorMessage: error.message })))
        ))
    ));

    getTokenPermissionCommands$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.getTokenPermissionCommands),
        switchMap((action) => this.permissionService.getTokenPermissionCommands(action.token).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.getTokenPermissionCommandsSuccess({ permission: resp.Data });
                } else {
                    console.error(resp.ErrorMessage);
                    return PermissionActions.getTokenPermissionCommandsFailed({
                        errorMessage: this.translate.instant('INVALID_PERMISSION')
                    });
                }
            }),
            catchError(error => of(PermissionActions.getTokenPermissionCommandsFailed({
                errorMessage: this.translate.instant('INVALID_PERMISSION')
            })))
        ))
    ));

    executeTokenCommand$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.executeTokenCommand),
        switchMap((action) => this.permissionService.executeCommand(action.token, action.pin, action.command.Id).pipe(
            map((resp) => {
                if (resp.Success) {
                    localStorage.setItem(
                        `${LOCALSTORAGE_PIN_KEY_PREFIX}${action.token}`,
                        action.pin
                    );
                    if (action.command.CommandId === 0) {
                        return PermissionActions.executeTokenCommandSuccess({
                            message: '',
                        });
                    } else {
                        return PermissionActions.executeTokenCommandSuccess({
                            message: this.translate.instant('COMMAND_EXECUTED', { description: action.command.CommandDescription }),
                        });
                    }
                } else {
                    if (action.command.CommandId === 0) {
                        return PermissionActions.executeTokenCommandPinCheckFailed({
                            errorMessage: resp.ErrorMessage,
                            errorCode: resp.ErrorCode,
                        });
                    } else {
                        return PermissionActions.executeTokenCommandFailed({
                            errorMessage: this.translate.instant('UNEXPECTED_ERROR_RETRY_LATER'),
                        });
                    }
                }
            }),
            catchError(error => of(
                PermissionActions.executeTokenCommandPinCheckFailed({
                    errorMessage: this.translate.instant('UNEXPECTED_ERROR_RETRY_LATER'),
                })))
        ))
    ));

    getPermissionTemplates$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.getPermissionTemplates),
        withLatestFrom(this.store.select('site', 'currentSite')),
        switchMap(([action, currentSite]) => {
            const sId = action.siteId ? action.siteId : currentSite.Id;
            return this.permissionService.getPermissionTemplates(sId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return PermissionActions.getPermissionTemplatesSuccess({ templates: resp.Data });
                    } else {
                        return PermissionActions.getPermissionTemplatesFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(PermissionActions.getPermissionTemplatesFailed({ errorMessage: error.message })
                )),
            );
        })
    ));

    savePermissionTemplates$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.savePermissionTemplate),
        withLatestFrom(
            this.store.select('site', 'currentSite'),
            this.store.select('user', 'currentTechnician')
        ),
        switchMap(([action, currentSite, currentTechnician]) => {
            let userId = action.userId;
            if (currentTechnician) {
                userId = currentTechnician.PersonifiedId;
            }
            const siteId = action.siteId ? action.siteId : currentSite.Id;
            if (action.logo) {
                return this.userFileService.insertUserFile(action.logo, userId).pipe(
                    map(resp => {
                        if (!resp.Success) {
                            return PermissionActions.savePermissionTemplateFailed({ errorMessage: resp.ErrorMessage });
                        }
                        if (!resp.Data) {
                            return PermissionActions.savePermissionTemplateFailed({ errorMessage: resp.ErrorMessage });
                        }
                        const template = { ...action.template, ImageId: resp.Data.Id };
                        return PermissionActions.saveEmailLogoSuccess({ siteId, template });
                    }),
                    catchError(error => of(PermissionActions.savePermissionTemplateFailed({ errorMessage: error.message })))
                );
            } else {
                return of(PermissionActions.saveEmailLogoSuccess({ siteId, template: { ...action.template } }));
            }
        })
    ));

    saveEmailLogoSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.saveEmailLogoSuccess),
        switchMap((action) => this.permissionService.savePermissionTemplates(action.siteId, action.template).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.savePermissionTemplateSuccess({
                        message: this.translate.instant('PERMISSION_EMAIL_TEMPLATE_SAVED')
                    });
                } else {
                    return PermissionActions.savePermissionTemplateFailed({
                        fileId: action.template.ImageId,
                        errorMessage: resp.ErrorMessage
                    });
                }
            }),
            catchError(error => of(PermissionActions.savePermissionTemplateFailed({
                fileId: action.template.ImageId,
                errorMessage: error.message
            })
            )),
        ))
    ));

    updatePermissionTemplates$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.updatePermissionTemplate),
        withLatestFrom(
            this.store.select('site', 'currentSite'),
            this.store.select('user', 'currentTechnician')
        ),
        switchMap(([action, currentSite, currentTechnician]) => {
            let userId = action.userId;
            if (currentTechnician) {
                userId = currentTechnician.PersonifiedId;
            }
            const siteId = action.siteId ? action.siteId : currentSite.Id;
            if (action.logo) {
                return this.userFileService.insertUserFile(action.logo, userId).pipe(
                    map(resp => {
                        if (!resp.Success) {
                            return PermissionActions.updatePermissionTemplateFailed({ errorMessage: resp.ErrorMessage });
                        }
                        if (!resp.Data) {
                            return PermissionActions.updatePermissionTemplateFailed({ errorMessage: resp.ErrorMessage });
                        }
                        const template = { ...action.template, ImageId: resp.Data.Id };
                        return PermissionActions.updateEmailLogoSuccess({ siteId, template });
                    }),
                    catchError(error => of(PermissionActions.updatePermissionTemplateFailed({ errorMessage: error.message })))
                );
            } else {
                return of(PermissionActions.updateEmailLogoSuccess({ siteId, template: action.template }));
            }
        })
    ));

    updateEmailLogoSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.updateEmailLogoSuccess),
        switchMap((action) => this.permissionService.updatePermissionTemplates(action.siteId, action.template).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.updatePermissionTemplateSuccess({
                        message: this.translate.instant('PERMISSION_EMAIL_TEMPLATE_SAVED')
                    });
                } else {
                    return PermissionActions.updatePermissionTemplateFailed({
                        errorMessage: resp.ErrorMessage
                    });
                }
            }),
            catchError(error => of(PermissionActions.updatePermissionTemplateFailed({
                errorMessage: error.message
            })
            )),
        ))
    ));

    removeLogoFile$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.savePermissionTemplateFailed),
        tap((action) => {
            if (action.fileId) {
                this.userFileService.deleteUserFile(action.fileId).pipe(take(1)).subscribe(data => console.log(data));
            }
        })
    ), { dispatch: false });

    getEmailLogo$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.getPermissionTemplateLogo),
        switchMap((action) => this.userFileService.getUserFileContent(action.fileId).pipe(
            map((resp) => PermissionActions.getPermissionTemplateLogoSuccess({ fileContent: resp })),
            catchError(error => of(PermissionActions.getPermissionTemplateLogoFailed({ errorMessage: error.message })))
        ))
    ));


    resetPinToken$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.resetPinToken),
        switchMap((action) => this.permissionService.resetPinToken(action.token).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.resetPinTokenSuccess({
                        message: '',
                    });
                } else {
                    return PermissionActions.resetPinTokenFailed({
                        errorMessage: this.translate.instant('UNEXPECTED_ERROR_RETRY_LATER'),
                    });
                }
            }),
            catchError(error => of(
                PermissionActions.resetPinTokenFailed({
                    errorMessage: this.translate.instant('UNEXPECTED_ERROR_RETRY_LATER'),
                })))
        ))
    ));



    deletePermissionExpired$ = createEffect(() => this.actions$.pipe(
        ofType(PermissionActions.deletePermissionExpired),
        switchMap((action) => this.permissionService.deletePermissionExpired(action.siteId).pipe(
            map((resp) => {
                if (resp.Success) {
                    return PermissionActions.deletePermissionExpiredSuccess({ message: this.translate.instant('DELETE_PERMISSION_EXPIRED_SUCCESS') });
                } else {
                    return PermissionActions.deletePermissionExpiredFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(PermissionActions.deletePermissionExpiredFailed({ errorMessage: error.message })))
        ))
    ));

    notifyStandard = createEffect(() => this.actions$.pipe(
        ofType(
            PermissionActions.permissionStatusNotification,
        ),
        tap((action) => {
            const notification = new Notification("PERMISSIONS", action.message, NotificationLevel.Info, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyFailure$ = createEffect(() => this.actions$.pipe(
        ofType(
            PermissionActions.setPinFailed,
            PermissionActions.resetPinFailed,
            PermissionActions.executeTokenCommandPinCheckFailed,
            PermissionActions.executeTokenCommandFailed,
            PermissionActions.addPermissionFailed,
            PermissionActions.editPermissionFailed,
            PermissionActions.deletePermissionFailed,
            PermissionActions.deletePermissionExpiredFailed,
            PermissionActions.savePermissionTemplateFailed,
            PermissionActions.updatePermissionTemplateFailed,
            PermissionActions.resetPinTokenFailed,
            PermissionActions.getTokenPermissionCommandsFailed,
        ),
        tap((action) => {
            const notification = new Notification("PERMISSIONS", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            PermissionActions.setPinSuccess,
            PermissionActions.resetPinSuccess,
            PermissionActions.executeTokenCommandSuccess,
            PermissionActions.addPermissionSuccess,
            PermissionActions.editPermissionSuccess,
            PermissionActions.deletePermissionSuccess,
            PermissionActions.deletePermissionExpiredSuccess,
            PermissionActions.savePermissionTemplateSuccess,
            PermissionActions.updatePermissionTemplateSuccess,
            PermissionActions.resetPinTokenSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("PERMISSIONS", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private translate: TranslateService,
        private permissionService: PermissionService,
        private userFileService: UserFileService,
    ) { }
}
